import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to debounce a value.
 * Uses deep comparison via JSON.stringify to avoid firing when object references
 * change but values stay the same.
 * @param {*} value - The value to debounce
 * @param {number} delay - Delay in milliseconds (default: 500)
 * @returns {*} - The debounced value
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);
  const previousValueRef = useRef(JSON.stringify(value));

  useEffect(() => {
    const currentValueStr = JSON.stringify(value);

    if (currentValueStr === previousValueRef.current) {
      // Value hasn't changed in a deep sense, do nothing
      return;
    }

    previousValueRef.current = currentValueStr;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}
